// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Alert, AlertTitle, Button, Divider,
  TextField, MenuItem, Tooltip, LinearProgress, Slider,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScienceIcon from '@mui/icons-material/Science';
import StorageIcon from '@mui/icons-material/Storage';
import SpeedIcon from '@mui/icons-material/Speed';
import store from './store';
import { triggerDownload } from './exports';

// ---------------------------------------------------------------------------
// Primordia — Pre-Migration Rightsizing.
//
// Why this screen exists: a lift-and-shift prices the CURRENT provisioned spec.
// On-premises those specs are close to free at the margin — a VM was given 8
// vCPU because 8 was the template default, and nobody paid for the seven idle
// ones. In the cloud every one of them is billed by the hour, forever. Carrying
// on-prem over-provisioning across the wire is the single most expensive
// unforced error in a migration, and it is invisible in a spec-only inventory.
//
// The honesty rules this file is built around, in priority order:
//
//   1. NEVER invent utilization. Provisioned specs (vCPU/RAM/disk) say what a
//      VM was GIVEN. They say nothing at all about what it USES. An inventory
//      with no telemetry cannot produce a rightsizing recommendation, and the
//      correct output in that case is "here is what to go collect", not a
//      confident-looking table. Sizing from provisioned specs alone does not
//      reduce anything — it faithfully reproduces the existing waste.
//
//   2. NEVER present a snapshot as a sustained measurement. A single
//      point-in-time reading is one frame of a movie. It can miss the nightly
//      batch, the month-end close, the 09:00 login storm entirely. Every number
//      derived from a snapshot on this screen is labelled a HYPOTHESIS, and the
//      label is repeated on the CSV export so it survives being emailed onward.
//      Nothing here is ever labelled p95, and nothing here is a committed size.
//
//   3. NEVER downsize from an average when a max column exists. The classic
//      rightsizing failure is a VM that averages 5% CPU and peaks at 100%:
//      average alone says "idle, shrink it", the peak says "it saturates". Every
//      classification and every target size below is driven by the PEAK columns
//      (cpuMaxPct / memMaxPct). If a peak column is missing for a row, that row
//      gets NO recommendation — it is reported as insufficient evidence.
//
//   4. ALWAYS show the evidence. Every recommended size sits on the same row as
//      the observed numbers that produced it, so the reader can disagree with
//      the arithmetic rather than having to trust it.
//
// Deliberately NOT in this file: money. Pricing lives in CostModel.js and is
// owned elsewhere. This screen produces resource deltas (vCPU, GB) which are the
// INPUT to that model. Multiplying a hypothesis by a rate card only produces a
// more expensive-looking hypothesis.
// ---------------------------------------------------------------------------

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';
const GREEN = '#2E7D32';
const AMBER = '#B26A00';
const RED = '#C62828';
const DASH = '—';

// Rendered-row cap. Tenants hold thousands of servers; every total, count and
// export on this screen covers the FULL set regardless of what is paged in.
const PAGE_SIZES = [50, 100, 200];
const DEFAULT_PAGE_SIZE = 200;

// Floors. Below these a cloud instance is rarely viable (agents, patching,
// monitoring and the OS itself need headroom), and a recommendation that lands
// under them is reported as clamped rather than silently rounded up.
const MIN_VCPU = 2;
const MIN_RAM_GB = 4;

// Standard cloud shape ladder. You cannot buy 3 vCPU. Rounding UP to a real
// purchasable shape keeps the recommendation actionable and keeps the savings
// number honest (it is always the conservative side of the arithmetic).
const VCPU_LADDER = [2, 4, 8, 16, 32, 48, 64, 96, 128];

const DEFAULT_THRESHOLDS = {
  headroomPct: 30,
  oversizedPeakCpu: 40,
  oversizedPeakMem: 50,
  undersizedPeakCpu: 90,
  undersizedPeakMem: 90,
  idleAvgCpu: 3,
  idlePeakCpu: 15,
  storageSlackPct: 50,
  storageSlackGb: 100,
  highIops: 500,
  highLatencyUs: 10000,
};

const CLASSES = {
  IDLE: 'Idle candidate',
  OVER: 'Oversized',
  RIGHT: 'Right-sized',
  UNDER: 'Undersized',
  UNKNOWN: 'Insufficient evidence',
};

const CLASS_COLOR = {
  [CLASSES.IDLE]: '#6A1B9A',
  [CLASSES.OVER]: BLUE,
  [CLASSES.RIGHT]: GREEN,
  [CLASSES.UNDER]: RED,
  [CLASSES.UNKNOWN]: GREY,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (n) => (n === null || n === undefined ? DASH : Math.round(n).toLocaleString());
const fmt1 = (n) => (n === null || n === undefined ? DASH : Number(n).toFixed(1));
const text = (v) => String(v === undefined || v === null ? '' : v).trim();

// A reading that is a number, optionally wearing a currency symbol, thousands
// separators, an exponent and/or a trailing unit ("1,234.56 GB", "85%",
// "1.2e3"). Anchored on purpose: utilization rows sit next to labels, so an OS
// string ("Windows Server 2019") or a cluster name ("prod-cluster-02") must
// come back as null rather than as the digits buried inside it.
const NUM_TEXT_RE = /^(?:[$€£¥]\s*)?(\d[\d,]*(?:\.\d+)?|\.\d+)(?:\s*[eE]([+-]?\d+))?\s*(?:%|[A-Za-z/µ]{1,8})?$/;

// Accounting notation: utilization and sizing extracts come out of finance-grade
// workbooks, which write a negative as "(1,234.56)", not "-1,234.56". Stripping
// the parentheses without negating does not lose the sign, it INVERTS it — the
// value returns positive and is wrong by twice itself. Keep the branch below.
// A blank, "-" or "N/A" stays null and never becomes 0: an absent measurement is
// unknown, and the two must not read the same downstream.
function num(v) {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  let s = String(v).trim();
  if (!s) return null;
  let neg = false;
  const wrapped = /^\((.*)\)$/.exec(s);
  if (wrapped) { neg = true; s = wrapped[1].trim(); }
  if (s[0] === '+' || s[0] === '-') { if (s[0] === '-') neg = !neg; s = s.slice(1).trim(); }
  const m = NUM_TEXT_RE.exec(s);
  if (!m) return null;
  const n = parseFloat(m[1].replace(/,/g, '') + (m[2] ? `e${m[2]}` : ''));
  if (!Number.isFinite(n)) return null;
  if (n === 0) return 0;
  return neg ? -n : n;
}

// Fact sheets reach the UI in two shapes depending on the loader that wrote
// them: the Postgres/DynamoDB rows carry a `data` jsonb object, the CSV/XLSX
// import path writes `fields`. Read BOTH and let `data` win where they overlap.
// Nothing is defaulted or fabricated here — a missing key stays missing.
export function specsOf(fs) {
  return { ...(fs.fields || {}), ...(fs.data || {}) };
}

// A server-ish IT component: anything the inventory loaders produced with a
// compute spec, or anything explicitly categorised as a Server.
function isServerRecord(d) {
  if (/^server$/i.test(text(d.category))) return true;
  return num(d.vcpu) !== null || num(d.ramGb) !== null || num(d.storageGb) !== null;
}

// Utilization reading for one server. `has` is true only when a real telemetry
// number is present (or the loader explicitly declared hasUtilization) — it is
// never inferred from the existence of a provisioned spec.
function utilOf(d) {
  const cpuAvg = num(d.cpuPct);
  const cpuMax = num(d.cpuMaxPct);
  const memAvg = num(d.memUtilPct);
  const memMax = num(d.memMaxPct);
  const declared = d.hasUtilization === true || text(d.hasUtilization).toLowerCase() === 'true';
  const anyReading = cpuAvg !== null || cpuMax !== null || memAvg !== null || memMax !== null;
  return { cpuAvg, cpuMax, memAvg, memMax, has: declared || anyReading };
}

function roundVcpuUp(raw) {
  const hit = VCPU_LADDER.find((v) => v >= raw);
  if (hit) return hit;
  return Math.ceil(raw / 16) * 16;
}

const roundRamUp = (raw) => Math.max(MIN_RAM_GB, Math.ceil(raw / 4) * 4);

// ---------------------------------------------------------------------------
// Analysis
//
// Peak-driven throughout. `t` is the live threshold set so every number on the
// screen can be traced to a value the user can see and change.
// ---------------------------------------------------------------------------
export function analyzeServers(servers, t) {
  const rows = servers.map((fs) => {
    const d = specsOf(fs);
    const u = utilOf(d);

    const vcpu = num(d.vcpu);
    const ramGb = num(d.ramGb);
    const storageGb = num(d.storageGb);
    const storageUsedGb = num(d.storageUsedGb);

    const readIops = num(d.readIops);
    const writeIops = num(d.writeIops);
    const totalIops = readIops === null && writeIops === null ? null : (readIops || 0) + (writeIops || 0);
    const readMBs = num(d.readMBs);
    const writeMBs = num(d.writeMBs);
    const readLatencyUs = num(d.readLatencyUs);
    const writeLatencyUs = num(d.writeLatencyUs);
    const worstLatencyUs = readLatencyUs === null && writeLatencyUs === null
      ? null : Math.max(readLatencyUs || 0, writeLatencyUs || 0);

    // --- Classification ---------------------------------------------------
    // A peak column is mandatory. Without it we refuse to classify: an average
    // on its own cannot distinguish a genuinely idle VM from a spiky one, and
    // guessing between those two is exactly how rightsizing breaks production.
    let cls = CLASSES.UNKNOWN;
    let why = '';
    const peaksPresent = u.cpuMax !== null && u.memMax !== null;

    if (!u.has) {
      why = 'No utilization telemetry on this record — provisioned specs only.';
    } else if (!peaksPresent) {
      const missing = [u.cpuMax === null ? 'CPU peak' : null, u.memMax === null ? 'memory peak' : null]
        .filter(Boolean).join(' and ');
      why = `Averages present but ${missing} missing. Sizing down from an average alone is not safe, so no recommendation is made.`;
    } else if (vcpu === null || ramGb === null) {
      cls = CLASSES.UNKNOWN;
      why = 'Utilization present but provisioned vCPU/RAM missing — a percentage of an unknown is still unknown.';
    } else if (u.cpuMax >= t.undersizedPeakCpu || u.memMax >= t.undersizedPeakMem) {
      cls = CLASSES.UNDER;
      why = `Peak CPU ${fmt1(u.cpuMax)}% / peak memory ${fmt1(u.memMax)}% reaches the undersized threshold (${t.undersizedPeakCpu}% / ${t.undersizedPeakMem}%). This VM saturates — do NOT shrink it.`;
    } else if (
      u.cpuAvg !== null && u.cpuAvg <= t.idleAvgCpu
      && u.cpuMax <= t.idlePeakCpu && u.memMax <= t.oversizedPeakMem
    ) {
      cls = CLASSES.IDLE;
      why = `Average CPU ${fmt1(u.cpuAvg)}% AND peak CPU ${fmt1(u.cpuMax)}% both under the idle thresholds (${t.idleAvgCpu}% / ${t.idlePeakCpu}%), memory peak ${fmt1(u.memMax)}%. Candidate for decommission or consolidation — confirm with the application owner first.`;
    } else if (u.cpuMax <= t.oversizedPeakCpu && u.memMax <= t.oversizedPeakMem) {
      cls = CLASSES.OVER;
      why = `Peak CPU ${fmt1(u.cpuMax)}% and peak memory ${fmt1(u.memMax)}% both below the oversized thresholds (${t.oversizedPeakCpu}% / ${t.oversizedPeakMem}%).`;
    } else {
      cls = CLASSES.RIGHT;
      why = `Peak CPU ${fmt1(u.cpuMax)}% / peak memory ${fmt1(u.memMax)}% sit between the oversized and undersized bands. Migrate as-is.`;
    }

    // --- Target size ------------------------------------------------------
    // Peak demand, plus headroom, rounded up to a purchasable shape.
    const canSize = cls !== CLASSES.UNKNOWN && vcpu !== null && ramGb !== null && peaksPresent;
    const factor = 1 + t.headroomPct / 100;
    let targetVcpu = null;
    let targetRam = null;
    let flooredVcpu = false;
    let flooredRam = false;

    if (canSize) {
      const rawVcpu = vcpu * (u.cpuMax / 100) * factor;
      const rawRam = ramGb * (u.memMax / 100) * factor;
      flooredVcpu = rawVcpu < MIN_VCPU;
      flooredRam = rawRam < MIN_RAM_GB;
      targetVcpu = roundVcpuUp(Math.max(rawVcpu, MIN_VCPU));
      targetRam = roundRamUp(Math.max(rawRam, MIN_RAM_GB));
      // An undersized VM must never be handed a smaller shape by the rounding.
      if (cls === CLASSES.UNDER) {
        targetVcpu = Math.max(targetVcpu, roundVcpuUp(vcpu + 1));
        targetRam = Math.max(targetRam, roundRamUp(ramGb * 1.25));
      }
    }

    const deltaVcpu = canSize ? targetVcpu - vcpu : null;
    const deltaRam = canSize ? targetRam - ramGb : null;

    // --- Storage slack ----------------------------------------------------
    const storageSlackGb = storageGb !== null && storageUsedGb !== null ? storageGb - storageUsedGb : null;
    const storageUsedPct = storageGb ? (storageUsedGb / storageGb) * 100 : null;
    const storageOverProvisioned = storageSlackGb !== null
      && storageSlackGb >= t.storageSlackGb
      && storageUsedPct !== null
      && storageUsedPct <= 100 - t.storageSlackPct;

    // --- Disk tier signal -------------------------------------------------
    // Capacity picks a disk SIZE. IOPS and latency pick the disk TIER, and that
    // is the decision that actually moves the bill and the SLA. A busy volume
    // already suffering latency on-prem will suffer worse on a burst-credit
    // tier — those must land on provisioned IOPS (io2 / Ultra / Premium v2).
    const busy = totalIops !== null && totalIops >= t.highIops;
    const slow = worstLatencyUs !== null && worstLatencyUs >= t.highLatencyUs;
    let diskTier = null;
    if (totalIops === null && worstLatencyUs === null) {
      diskTier = null;
    } else if (busy && slow) {
      diskTier = 'Provisioned IOPS required';
    } else if (busy) {
      diskTier = 'Provisioned IOPS — review';
    } else if (slow) {
      diskTier = 'High latency — investigate';
    } else {
      diskTier = 'General purpose (gp3-class) likely sufficient';
    }

    return {
      id: fs.id,
      name: fs.name,
      cluster: text(d.cluster),
      os: text(d.os),
      powerState: text(d.powerState),
      sourceSystem: text(d.sourceSystem),
      sample: text(d.utilizationSample),
      units: text(d.utilizationUnits),
      diskCount: Array.isArray(d.disks) ? d.disks.length : null,
      vcpu,
      ramGb,
      storageGb,
      storageUsedGb,
      storageSlackGb,
      storageUsedPct,
      storageOverProvisioned,
      cpuAvg: u.cpuAvg,
      cpuMax: u.cpuMax,
      memAvg: u.memAvg,
      memMax: u.memMax,
      memActiveMib: num(d.memActiveMib),
      memConsumedMib: num(d.memConsumedMib),
      readIops,
      writeIops,
      totalIops,
      readMBs,
      writeMBs,
      readLatencyUs,
      writeLatencyUs,
      worstLatencyUs,
      rxMbits: num(d.rxMbits),
      txMbits: num(d.txMbits),
      hasUtil: u.has,
      cls,
      why,
      canSize,
      targetVcpu,
      targetRam,
      deltaVcpu,
      deltaRam,
      flooredVcpu,
      flooredRam,
      diskTier,
    };
  });

  // --- Totals. Savings are counted ONLY over rows we were willing to size.
  const sized = rows.filter((r) => r.canSize);
  const totals = {
    servers: rows.length,
    withUtil: rows.filter((r) => r.hasUtil).length,
    sized: sized.length,
    unsized: rows.length - sized.length,
    curVcpu: 0,
    curRam: 0,
    curStorage: 0,
    tgtVcpu: 0,
    tgtRam: 0,
    reclaimStorage: 0,
    reclaimCount: 0,
    floored: rows.filter((r) => r.flooredVcpu || r.flooredRam).length,
    provIops: rows.filter((r) => r.diskTier === 'Provisioned IOPS required').length,
  };
  rows.forEach((r) => {
    if (r.storageGb !== null) totals.curStorage += r.storageGb;
    if (r.storageOverProvisioned) { totals.reclaimStorage += r.storageSlackGb; totals.reclaimCount += 1; }
  });
  sized.forEach((r) => {
    totals.curVcpu += r.vcpu;
    totals.curRam += r.ramGb;
    totals.tgtVcpu += r.targetVcpu;
    totals.tgtRam += r.targetRam;
  });
  totals.vcpuReductionPct = totals.curVcpu ? ((totals.curVcpu - totals.tgtVcpu) / totals.curVcpu) * 100 : 0;
  totals.ramReductionPct = totals.curRam ? ((totals.curRam - totals.tgtRam) / totals.curRam) * 100 : 0;

  const byClass = {};
  Object.values(CLASSES).forEach((c) => { byClass[c] = 0; });
  rows.forEach((r) => { byClass[r.cls] += 1; });

  return { rows, totals, byClass };
}

// ---------------------------------------------------------------------------
// CSV — the caveat travels with the file
// ---------------------------------------------------------------------------
function csvCell(v) {
  if (v === undefined || v === null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsvWithBanner(filename, bannerLines, columns, rows) {
  const banner = bannerLines.map((l) => `# ${l}`).join('\n');
  const header = columns.map((c) => csvCell(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => csvCell(c.value(r))).join(',')).join('\n');
  const blob = new Blob([`${banner}\n\n${header}\n${body}\n`], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, filename);
}

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------
function StatCard({ label, value, note, accent }) {
  return (
    <Paper sx={{ p: 1.25, borderTop: `3px solid ${accent}` }}>
      <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13 }}>{label}</Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 700, color: NAVY, lineHeight: 1.2 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: GREY }}>{note}</Typography>
    </Paper>
  );
}

function ThresholdField({ label, value, onChange, adornment = '%', help }) {
  return (
    <Tooltip title={help || ''} placement="top">
      <TextField
        size="small"
        type="number"
        label={label}
        value={value}
        onChange={(e) => onChange(num(e.target.value))}
        InputProps={{ endAdornment: <span style={{ color: GREY, fontSize: 12 }}>{adornment}</span> }}
        sx={{ width: 178 }}
      />
    </Tooltip>
  );
}

function ClassChip({ cls }) {
  return (
    <Chip
      size="small"
      label={cls}
      sx={{
        height: 20,
        fontWeight: 700,
        fontSize: 11,
        bgcolor: 'transparent',
        border: `1px solid ${CLASS_COLOR[cls]}`,
        color: CLASS_COLOR[cls],
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function Rightsizing({ tenantId }) {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [t, setT] = useState(DEFAULT_THRESHOLDS);
  const [classFilter, setClassFilter] = useState('All');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setLoadErr('');
    store.listFactSheets(tenantId, { type: 'ITComponent' })
      .then((rows) => {
        if (!live) return;
        setServers((rows || []).filter((fs) => isServerRecord(specsOf(fs))));
        setLoading(false);
      })
      .catch(() => {
        if (!live) return;
        setLoadErr('Could not load the server inventory for this workspace. Reload the page and try again.');
        setLoading(false);
      });
    return () => { live = false; };
  }, [tenantId]);

  const setThreshold = (key) => (v) => {
    setT((prev) => ({ ...prev, [key]: v === null ? DEFAULT_THRESHOLDS[key] : v }));
    setPage(0);
  };

  const analysis = useMemo(() => analyzeServers(servers, t), [servers, t]);
  const { rows, totals, byClass } = analysis;

  // --- Data-quality gate ---------------------------------------------------
  const utilRows = useMemo(() => rows.filter((r) => r.hasUtil), [rows]);
  const hasAnyUtil = utilRows.length > 0;

  const sources = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.sourceSystem) set.add(r.sourceSystem); });
    return [...set].sort();
  }, [rows]);

  // The sampling basis, read verbatim off the records. If the loader did not
  // declare one, that absence is itself reported — an undeclared basis is
  // treated as a point-in-time snapshot, never as a sustained measurement.
  const sampleNotes = useMemo(() => {
    const set = new Set();
    utilRows.forEach((r) => { if (r.sample) set.add(r.sample); });
    return [...set];
  }, [utilRows]);

  const unitNotes = useMemo(() => {
    const set = new Set();
    utilRows.forEach((r) => { if (r.units) set.add(r.units); });
    return [...set];
  }, [utilRows]);

  const missingPeaks = useMemo(
    () => utilRows.filter((r) => r.cpuMax === null || r.memMax === null).length,
    [utilRows]
  );

  const storageRows = useMemo(
    () => rows.filter((r) => r.storageOverProvisioned).sort((a, b) => b.storageSlackGb - a.storageSlackGb),
    [rows]
  );
  const hasStorageUsed = useMemo(() => rows.some((r) => r.storageUsedGb !== null), [rows]);

  const iopsRows = useMemo(
    () => rows.filter((r) => r.totalIops !== null || r.worstLatencyUs !== null)
      .sort((a, b) => (b.totalIops || 0) - (a.totalIops || 0)),
    [rows]
  );

  const visibleRows = useMemo(
    () => (classFilter === 'All' ? utilRows : utilRows.filter((r) => r.cls === classFilter)),
    [utilRows, classFilter]
  );

  const pagedRows = useMemo(
    () => visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [visibleRows, page, rowsPerPage]
  );

  // --- Caveat text, reused on screen AND in the export ---------------------
  const caveatLines = useMemo(() => {
    const lines = [
      'PRIMORDIA PRE-MIGRATION RIGHTSIZING — HYPOTHESIS, NOT A COMMITTED RECOMMENDATION.',
      'The utilization readings behind these numbers are a POINT-IN-TIME SNAPSHOT.',
      'They are NOT a p95 and NOT a sustained measurement over any observation window.',
      'A single sample can miss nightly batch, month-end close and login storms entirely,',
      'so it can understate true peak demand badly. Validate against 2-4 weeks of',
      'performance data (p95) before any of these sizes are committed or purchased.',
      `Thresholds in use: oversized peak CPU <= ${t.oversizedPeakCpu}%, oversized peak memory <= ${t.oversizedPeakMem}%,`,
      `undersized peak CPU >= ${t.undersizedPeakCpu}%, undersized peak memory >= ${t.undersizedPeakMem}%,`,
      `idle = average CPU <= ${t.idleAvgCpu}% AND peak CPU <= ${t.idlePeakCpu}%, headroom applied ${t.headroomPct}%.`,
      `Floors: no size below ${MIN_VCPU} vCPU / ${MIN_RAM_GB} GB without being flagged as clamped.`,
      'Target sizes are derived from the PEAK columns, never from averages alone.',
      'Resource deltas only — no pricing is applied here. Cost modelling lives in the Cost Model screen.',
    ];
    if (sampleNotes.length) sampleNotes.forEach((s) => lines.push(`Sample basis recorded on the data: ${s}`));
    else lines.push('Sample basis: NOT DECLARED on the source records — treated as point-in-time until proven otherwise.');
    if (unitNotes.length) unitNotes.forEach((s) => lines.push(`Units recorded on the data: ${s}`));
    return lines;
  }, [t, sampleNotes, unitNotes]);

  const exportColumns = [
    { label: 'Server', value: (r) => r.name },
    { label: 'Cluster', value: (r) => r.cluster },
    { label: 'OS', value: (r) => r.os },
    { label: 'Power state', value: (r) => r.powerState },
    { label: 'Source system', value: (r) => r.sourceSystem },
    { label: 'Provisioned vCPU', value: (r) => r.vcpu },
    { label: 'Provisioned RAM (GB)', value: (r) => r.ramGb },
    { label: 'Provisioned storage (GB)', value: (r) => r.storageGb },
    { label: 'Storage used (GB)', value: (r) => r.storageUsedGb },
    { label: 'Storage slack (GB)', value: (r) => (r.storageSlackGb === null ? '' : Math.round(r.storageSlackGb)) },
    { label: 'Observed CPU avg (%)', value: (r) => r.cpuAvg },
    { label: 'Observed CPU peak (%)', value: (r) => r.cpuMax },
    { label: 'Observed memory avg (%)', value: (r) => r.memAvg },
    { label: 'Observed memory peak (%)', value: (r) => r.memMax },
    { label: 'Active memory (MiB)', value: (r) => r.memActiveMib },
    { label: 'Consumed memory (MiB)', value: (r) => r.memConsumedMib },
    { label: 'Read IOPS', value: (r) => r.readIops },
    { label: 'Write IOPS', value: (r) => r.writeIops },
    { label: 'Total IOPS', value: (r) => r.totalIops },
    { label: 'Read MB/s', value: (r) => r.readMBs },
    { label: 'Write MB/s', value: (r) => r.writeMBs },
    { label: 'Read latency (us)', value: (r) => r.readLatencyUs },
    { label: 'Write latency (us)', value: (r) => r.writeLatencyUs },
    { label: 'RX (Mbit/s)', value: (r) => r.rxMbits },
    { label: 'TX (Mbit/s)', value: (r) => r.txMbits },
    { label: 'Disks', value: (r) => r.diskCount },
    { label: 'Classification', value: (r) => r.cls },
    { label: 'Candidate vCPU (hypothesis)', value: (r) => (r.canSize ? r.targetVcpu : '') },
    { label: 'Candidate RAM GB (hypothesis)', value: (r) => (r.canSize ? r.targetRam : '') },
    { label: 'vCPU delta', value: (r) => (r.canSize ? r.deltaVcpu : '') },
    { label: 'RAM GB delta', value: (r) => (r.canSize ? r.deltaRam : '') },
    { label: 'Clamped to floor', value: (r) => (r.flooredVcpu || r.flooredRam ? 'Yes' : '') },
    { label: 'Disk tier signal', value: (r) => r.diskTier || '' },
    { label: 'Sample basis', value: (r) => r.sample || 'not declared — point-in-time assumed' },
    { label: 'Evidence / reason', value: (r) => r.why },
    { label: 'Status', value: () => 'HYPOTHESIS — snapshot-derived, not a p95, not committed' },
  ];

  const exportAnalysis = () => downloadCsvWithBanner(
    `rightsizing-hypothesis-${tenantId}.csv`, caveatLines, exportColumns, visibleRows
  );

  const exportInventoryOnly = () => downloadCsvWithBanner(
    `provisioned-specs-no-utilization-${tenantId}.csv`,
    [
      'PRIMORDIA — PROVISIONED SPECS ONLY. NO UTILIZATION DATA IS AVAILABLE FOR THIS WORKSPACE.',
      'This file contains what each VM was GIVEN, not what it USES.',
      'It CANNOT be used to rightsize: sizing from provisioned specs alone reproduces the',
      'existing on-premises over-provisioning at cloud prices.',
      'Collect vCenter performance statistics, CloudWatch/Azure Monitor, or an assessment',
      'tool export over a 2-4 week window capturing p95 before any sizing decision.',
    ],
    [
      { label: 'Server', value: (r) => r.name },
      { label: 'Cluster', value: (r) => r.cluster },
      { label: 'OS', value: (r) => r.os },
      { label: 'Power state', value: (r) => r.powerState },
      { label: 'Source system', value: (r) => r.sourceSystem },
      { label: 'Provisioned vCPU', value: (r) => r.vcpu },
      { label: 'Provisioned RAM (GB)', value: (r) => r.ramGb },
      { label: 'Provisioned storage (GB)', value: (r) => r.storageGb },
      { label: 'Utilization available', value: () => 'No' },
    ],
    rows
  );

  const title = (
    <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY, mb: 0.5 }}>
      Pre-Migration Rightsizing
    </Typography>
  );

  if (loading) {
    return <Box sx={{ p: 2 }}>{title}<LinearProgress sx={{ mt: 2 }} /></Box>;
  }

  if (loadErr) {
    return <Box sx={{ p: 2 }}>{title}<Alert severity="error" sx={{ mt: 2, maxWidth: 820 }}>{loadErr}</Alert></Box>;
  }

  if (!rows.length) {
    return (
      <Box sx={{ p: 2 }}>
        {title}
        <Alert severity="info" sx={{ mt: 2, maxWidth: 900 }}>
          <AlertTitle>No server inventory in this workspace</AlertTitle>
          Load an RVTools export or an equivalent virtualisation inventory from the Infrastructure Import
          screen first. Rightsizing needs, at minimum, provisioned vCPU / RAM / storage per server — and to
          produce an actual recommendation it also needs utilization telemetry.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {title}
      <Typography sx={{ fontSize: 13, color: GREY, mb: 2, maxWidth: 980 }}>
        Rightsize BEFORE the migration, not after. A lift-and-shift prices whatever spec the VM carries today —
        on-premises an idle vCPU was free, in the cloud it is billed every hour forever. This screen only makes a
        recommendation where there is measured evidence to support one, and says so plainly where there is not.
      </Typography>

      {/* =================================================================== */}
      {/* 1. DATA-QUALITY GATE — first, and unmissable                        */}
      {/* =================================================================== */}
      {!hasAnyUtil ? (
        <>
          <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2, maxWidth: 1100 }}>
            <AlertTitle sx={{ fontWeight: 800 }}>
              Rightsizing is not possible for this workspace — there is no utilization data
            </AlertTitle>
            <Typography sx={{ fontSize: 13, mb: 1 }}>
              All {fmt(rows.length)} server records here carry <b>provisioned specifications only</b>
              {sources.length ? <> (source: {sources.join(', ')})</> : null}. A provisioned spec says what a VM
              was <b>given</b>. It says nothing whatsoever about what that VM <b>uses</b>.
            </Typography>
            <Typography sx={{ fontSize: 13, mb: 1 }}>
              No usage-based recommendation is offered below, and none should be constructed from this data.
              Sizing from provisioned specs alone does not reduce anything — it faithfully copies the existing
              on-premises over-provisioning into the cloud and then bills you for it monthly. The 8 vCPU that a
              template handed out in 2019 becomes 8 vCPU you pay for every hour of every month.
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>What to collect before sizing:</Typography>
            <Box component="ul" sx={{ fontSize: 13, pl: 2.5, m: 0 }}>
              <li>
                <b>vCenter performance statistics</b> — CPU and memory usage, average <i>and</i> maximum, per VM.
                Raise the statistics level / retention if the rollups have already discarded the detail.
              </li>
              <li>
                <b>CloudWatch or Azure Monitor</b> for anything already running in a cloud account.
              </li>
              <li>
                <b>An assessment tool export</b> — Azure Migrate, AWS Application Discovery Service, Device42,
                Cloudamize, Movere or similar — run continuously rather than as a one-off poll.
              </li>
              <li>
                <b>A 2-4 week observation window</b> that spans a month-end close and any batch cycle, reported
                at <b>p95</b> (not average, not a single reading). Two weeks is the minimum that captures a
                weekly cycle; four weeks is what captures a monthly one.
              </li>
              <li>
                <b>Storage used vs provisioned, and IOPS / throughput / latency per volume</b> — capacity picks
                the disk size, but IOPS and latency pick the disk tier, and the tier is what costs money.
              </li>
            </Box>
          </Alert>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 1.5, mb: 2 }}>
            <StatCard label="Servers in inventory" value={fmt(rows.length)} accent={BLUE}
              note={sources.length ? `Source: ${sources.join(', ')}` : 'Source system not recorded'} />
            <StatCard label="With utilization telemetry" value="0" accent={RED}
              note="No recommendation can be made for any server" />
            <StatCard label="Provisioned vCPU" value={fmt(rows.reduce((a, r) => a + (r.vcpu || 0), 0))} accent={GREY}
              note="What was allocated — not what is used" />
            <StatCard label="Provisioned RAM (GB)" value={fmt(rows.reduce((a, r) => a + (r.ramGb || 0), 0))} accent={GREY}
              note="What was allocated — not what is used" />
            <StatCard label="Provisioned storage (GB)" value={fmt(totals.curStorage)} accent={GREY}
              note={hasStorageUsed ? 'Used vs provisioned available below' : 'Storage used not captured by this source'} />
          </Box>

          <Button size="small" startIcon={<DownloadIcon />} variant="outlined" onClick={exportInventoryOnly}>
            Export provisioned specs (clearly marked: not a sizing basis)
          </Button>
        </>
      ) : (
        <>
          {/* =============================================================== */}
          {/* 2. SNAPSHOT WARNING                                             */}
          {/* =============================================================== */}
          <Alert severity="warning" icon={<ScienceIcon />} sx={{ mb: 2, maxWidth: 1100 }}>
            <AlertTitle sx={{ fontWeight: 800 }}>
              These readings are a point-in-time snapshot — everything below is a hypothesis, not a finding
            </AlertTitle>
            <Typography sx={{ fontSize: 13, mb: 1 }}>
              {sampleNotes.length ? (
                <>
                  The source records declare their sampling basis as:{' '}
                  {sampleNotes.map((s) => (
                    <Chip key={s} size="small" label={s}
                      sx={{ height: 20, fontSize: 11, fontWeight: 700, mr: 0.5, bgcolor: '#FFF4E5', color: AMBER }} />
                  ))}
                </>
              ) : (
                <>
                  The source records do <b>not</b> declare a sampling window. An undeclared basis is treated here
                  as a single point-in-time reading, because that is the conservative assumption.
                </>
              )}
            </Typography>
            <Typography sx={{ fontSize: 13, mb: 1 }}>
              A single sample is one frame of a movie. It can miss the nightly batch, the month-end close and the
              morning login storm entirely, so it can <b>understate true peak demand badly</b>. Nothing on this
              screen is a p95, and nothing on this screen is a committed recommendation. Treat every candidate
              size as a question to validate against 2-4 weeks of performance data before anyone buys anything.
            </Typography>
            {unitNotes.length ? (
              <Typography sx={{ fontSize: 12, color: GREY }}>
                Units as recorded on the data: {unitNotes.join(' · ')}
              </Typography>
            ) : null}
          </Alert>

          {utilRows.length < rows.length && (
            <Alert severity="info" sx={{ mb: 2, maxWidth: 1100 }}>
              <b>{fmt(rows.length - utilRows.length)}</b> of {fmt(rows.length)} servers in this workspace carry no
              utilization telemetry at all and are excluded from every recommendation and every savings total
              below. They are not "fine" — they are unmeasured. Collect performance data for them before sizing.
            </Alert>
          )}

          {missingPeaks > 0 && (
            <Alert severity="warning" sx={{ mb: 2, maxWidth: 1100 }}>
              <b>{fmt(missingPeaks)}</b> servers have average utilization but no peak column. They are reported as
              <b> insufficient evidence</b> and given no target size: a VM averaging 5% CPU may be genuinely idle
              or may peak at 100% every night, and an average cannot tell those apart. Downsizing the second one
              takes production with it.
            </Alert>
          )}

          {/* =============================================================== */}
          {/* 3. THRESHOLDS — visible and adjustable                          */}
          {/* =============================================================== */}
          <Paper sx={{ p: 1.5, mb: 2 }}>
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 14, mb: 0.5 }}>
              Classification thresholds in use
            </Typography>
            <Typography sx={{ fontSize: 12, color: GREY, mb: 1.5, maxWidth: 980 }}>
              Every classification below is driven by the <b>peak</b> columns (CPU max %, memory max %), never by
              the average alone. The average is used only to confirm an idle candidate, and only when the peak
              agrees with it. Change a rule here rather than overriding an answer in the table.
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1.5 }}>
              <ThresholdField label="Oversized: peak CPU ≤" value={t.oversizedPeakCpu}
                onChange={setThreshold('oversizedPeakCpu')}
                help="A VM whose peak CPU never reaches this is a downsize candidate." />
              <ThresholdField label="Oversized: peak mem ≤" value={t.oversizedPeakMem}
                onChange={setThreshold('oversizedPeakMem')}
                help="Both CPU and memory peaks must be under their oversized thresholds." />
              <ThresholdField label="Undersized: peak CPU ≥" value={t.undersizedPeakCpu}
                onChange={setThreshold('undersizedPeakCpu')}
                help="At or above this the VM saturates and must not be shrunk." />
              <ThresholdField label="Undersized: peak mem ≥" value={t.undersizedPeakMem}
                onChange={setThreshold('undersizedPeakMem')}
                help="At or above this the VM saturates and must not be shrunk." />
              <ThresholdField label="Idle: average CPU ≤" value={t.idleAvgCpu}
                onChange={setThreshold('idleAvgCpu')}
                help="Necessary but NOT sufficient — the peak test below must also pass." />
              <ThresholdField label="Idle: peak CPU ≤" value={t.idlePeakCpu}
                onChange={setThreshold('idlePeakCpu')}
                help="The guard against calling a spiky VM idle. A VM averaging 5% but peaking at 100% fails this test and is never classed idle." />
              <ThresholdField label="Storage slack ≥" value={t.storageSlackPct}
                onChange={setThreshold('storageSlackPct')}
                help="Flag a volume when this share of provisioned capacity is unused." />
              <ThresholdField label="Storage slack min" value={t.storageSlackGb}
                onChange={setThreshold('storageSlackGb')} adornment="GB"
                help="Ignore small absolute slack so the list stays actionable." />
              <ThresholdField label="High IOPS ≥" value={t.highIops}
                onChange={setThreshold('highIops')} adornment="IO/s"
                help="Read + write IOPS at or above this suggests provisioned-IOPS storage." />
              <ThresholdField label="High latency ≥" value={t.highLatencyUs}
                onChange={setThreshold('highLatencyUs')} adornment="µs"
                help="Worst of read/write latency. 10,000 µs = 10 ms." />
            </Stack>

            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ maxWidth: 420 }}>
              <Typography sx={{ fontSize: 12, color: GREY, fontWeight: 600 }}>
                Headroom above observed peak: <b style={{ color: NAVY }}>{t.headroomPct}%</b>
              </Typography>
              <Slider size="small" min={0} max={100} step={5} value={t.headroomPct}
                onChange={(e, v) => setT((prev) => ({ ...prev, headroomPct: v }))}
                valueLabelDisplay="auto" sx={{ color: BLUE }} />
              <Typography sx={{ fontSize: 11, color: GREY }}>
                Target = provisioned × observed peak % × (1 + headroom), rounded UP to a purchasable shape.
                Because the peak here comes from a snapshot rather than a p95, headroom is doing more work than
                it normally would — it is the only cushion against a peak the sample never saw.
              </Typography>
            </Box>
          </Paper>

          {/* =============================================================== */}
          {/* 4. SAVINGS — in resource units only                             */}
          {/* =============================================================== */}
          <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 15, mb: 1 }}>
            Provisioned vs candidate footprint
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 1.5, mb: 1.5 }}>
            <StatCard label="Servers sized" value={fmt(totals.sized)} accent={BLUE}
              note={`${fmt(totals.unsized)} of ${fmt(totals.servers)} excluded — no telemetry or no peak column`} />
            <StatCard label="vCPU: provisioned → candidate"
              value={`${fmt(totals.curVcpu)} → ${fmt(totals.tgtVcpu)}`}
              accent={totals.vcpuReductionPct > 0 ? GREEN : AMBER}
              note={`${totals.vcpuReductionPct >= 0 ? '−' : '+'}${fmt1(Math.abs(totals.vcpuReductionPct))}% across sized servers`} />
            <StatCard label="RAM GB: provisioned → candidate"
              value={`${fmt(totals.curRam)} → ${fmt(totals.tgtRam)}`}
              accent={totals.ramReductionPct > 0 ? GREEN : AMBER}
              note={`${totals.ramReductionPct >= 0 ? '−' : '+'}${fmt1(Math.abs(totals.ramReductionPct))}% across sized servers`} />
            <StatCard label="Storage reclaimable (GB)" value={fmt(totals.reclaimStorage)} accent={totals.reclaimCount ? GREEN : GREY}
              note={hasStorageUsed
                ? `${fmt(totals.reclaimCount)} volumes provisioned well above what they use`
                : 'Storage used not captured by this inventory source'} />
            <StatCard label="Clamped to the floor" value={fmt(totals.floored)} accent={totals.floored ? AMBER : GREEN}
              note={`Computed below ${MIN_VCPU} vCPU / ${MIN_RAM_GB} GB — raised to the floor and flagged`} />
          </Box>

          <Alert severity="info" sx={{ mb: 2, maxWidth: 1100 }}>
            <b>Resource units only — no dollars are calculated here, deliberately.</b> Pricing lives in the Cost
            Model screen, which owns the rate card, the region, the commitment terms and the licensing uplifts.
            The vCPU and GB deltas above are the <i>input</i> to that model. Multiplying a snapshot-derived
            hypothesis by a rate card does not make it a saving — it makes it a more expensive-looking hypothesis.
            Validate the sizes against p95 data first, then price them.
          </Alert>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1.5, mb: 2 }}>
            {Object.values(CLASSES).map((c) => (
              <Paper key={c} sx={{ p: 1.25, borderTop: `3px solid ${CLASS_COLOR[c]}` }}>
                <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13 }}>{c}</Typography>
                <Typography sx={{ fontSize: 24, fontWeight: 700, color: NAVY, lineHeight: 1.2 }}>
                  {fmt(byClass[c])}
                </Typography>
                <Typography sx={{ fontSize: 11, color: GREY }}>
                  {c === CLASSES.IDLE && 'Low average AND low peak — confirm with the app owner before acting'}
                  {c === CLASSES.OVER && 'Peak demand well under provisioned spec'}
                  {c === CLASSES.RIGHT && 'Migrate as-is'}
                  {c === CLASSES.UNDER && 'Saturating — size UP, do not shrink'}
                  {c === CLASSES.UNKNOWN && 'No telemetry or no peak column — not a recommendation'}
                </Typography>
              </Paper>
            ))}
          </Box>

          {/* =============================================================== */}
          {/* 5. STORAGE & IOPS                                               */}
          {/* =============================================================== */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <StorageIcon sx={{ fontSize: 18, color: BLUE }} />
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 15 }}>
              Storage over-provisioning
            </Typography>
            <Chip size="small" label={fmt(storageRows.length)} sx={{ height: 18 }} />
          </Stack>
          {!hasStorageUsed ? (
            <Alert severity="info" sx={{ mb: 2, maxWidth: 1100 }}>
              This inventory does not carry <code>storageUsedGb</code>, so provisioned-versus-used cannot be
              compared. Capture used capacity per VM (RVTools vDisk / vInfo, or the assessment tool export) —
              thick-provisioned volumes that are 20% full are one of the easiest reductions available before a
              migration, and one of the most expensive to carry across as-is.
            </Alert>
          ) : !storageRows.length ? (
            <Alert severity="success" sx={{ mb: 2, maxWidth: 1100 }}>
              No volume exceeds its used capacity by {t.storageSlackPct}% and {fmt(t.storageSlackGb)} GB.
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Server</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Provisioned GB</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Used GB</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Used %</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Reclaimable GB</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Disks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storageRows.slice(0, 25).map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                      <TableCell align="right">{fmt(r.storageGb)}</TableCell>
                      <TableCell align="right">{fmt(r.storageUsedGb)}</TableCell>
                      <TableCell align="right" sx={{ color: AMBER, fontWeight: 700 }}>{fmt1(r.storageUsedPct)}%</TableCell>
                      <TableCell align="right" sx={{ color: GREEN, fontWeight: 700 }}>{fmt(r.storageSlackGb)}</TableCell>
                      <TableCell align="right">{r.diskCount === null ? DASH : r.diskCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {storageRows.length > 25 && (
                <Typography variant="caption" sx={{ color: AMBER, display: 'block', p: 1 }}>
                  Showing the 25 largest of {fmt(storageRows.length)} over-provisioned volumes — the totals and the
                  CSV export cover all of them.
                </Typography>
              )}
            </TableContainer>
          )}

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <SpeedIcon sx={{ fontSize: 18, color: BLUE }} />
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 15 }}>
              Observed IOPS, throughput and latency — the disk TIER decision
            </Typography>
            <Chip size="small" label={fmt(totals.provIops)} sx={{ height: 18, bgcolor: totals.provIops ? '#FFF4E5' : undefined }} />
          </Stack>
          {!iopsRows.length ? (
            <Alert severity="info" sx={{ mb: 2, maxWidth: 1100 }}>
              No IOPS, throughput or latency readings are present on this inventory. Disk <i>capacity</i> can be
              matched without them, but the disk <i>tier</i> cannot — and the tier drives both the bill and the
              application's response time far more than capacity does. Collect per-volume IOPS, MB/s and latency
              before choosing between gp3-class and provisioned-IOPS storage.
            </Alert>
          ) : (
            <>
              <Alert severity={totals.provIops ? 'warning' : 'info'} sx={{ mb: 1, maxWidth: 1100 }}>
                <b>{fmt(totals.provIops)}</b> servers show <b>both</b> high IOPS (≥ {fmt(t.highIops)} IO/s) and high
                latency (≥ {fmt(t.highLatencyUs)} µs). These need <b>provisioned-IOPS storage</b> (io2 / io2 Block
                Express, Azure Ultra Disk or Premium SSD v2) — not gp3-class. They are already latency-bound on
                dedicated on-premises arrays; putting them on a burst-credit tier will make them measurably worse
                the first time the credits run out, and that failure appears as an application problem, not a
                storage one. Capacity is the wrong axis for these servers.
              </Alert>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Server</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Read IOPS</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Write IOPS</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Total IOPS</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>R / W MB/s</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>R / W latency µs</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Tier signal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {iopsRows.slice(0, 25).map((r) => {
                      const urgent = r.diskTier === 'Provisioned IOPS required';
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                          <TableCell align="right">{fmt(r.readIops)}</TableCell>
                          <TableCell align="right">{fmt(r.writeIops)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(r.totalIops)}</TableCell>
                          <TableCell align="right">{fmt1(r.readMBs)} / {fmt1(r.writeMBs)}</TableCell>
                          <TableCell align="right" sx={{ color: r.worstLatencyUs >= t.highLatencyUs ? RED : undefined }}>
                            {fmt(r.readLatencyUs)} / {fmt(r.writeLatencyUs)}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: urgent ? RED : GREY, fontWeight: urgent ? 700 : 400 }}>
                            {r.diskTier}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {iopsRows.length > 25 && (
                  <Typography variant="caption" sx={{ color: AMBER, display: 'block', p: 1 }}>
                    Showing the 25 busiest of {fmt(iopsRows.length)} servers with I/O readings — the CSV export
                    covers all of them.
                  </Typography>
                )}
              </TableContainer>
            </>
          )}

          {/* =============================================================== */}
          {/* 6. EVIDENCE TABLE                                               */}
          {/* =============================================================== */}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 15 }}>
              Per-server evidence
            </Typography>
            <TextField
              select size="small" label="Classification" value={classFilter}
              onChange={(e) => { setClassFilter(e.target.value); setPage(0); }}
              sx={{ width: 220 }}
            >
              <MenuItem value="All">All ({fmt(utilRows.length)})</MenuItem>
              {Object.values(CLASSES).map((c) => (
                <MenuItem key={c} value={c}>{c} ({fmt(byClass[c])})</MenuItem>
              ))}
            </TextField>
            <Button size="small" startIcon={<DownloadIcon />} variant="outlined" onClick={exportAnalysis}>
              CSV with evidence ({fmt(visibleRows.length)})
            </Button>
            <Typography sx={{ fontSize: 11, color: AMBER, fontWeight: 700 }}>
              The export carries the snapshot caveat in its header — do not strip it.
            </Typography>
          </Stack>

          <TableContainer component={Paper}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Server</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Cluster / OS</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Provisioned<br />vCPU / GB</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>CPU %<br />avg / peak</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Mem %<br />avg / peak</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Total<br />IOPS</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Classification</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Candidate<br />vCPU / GB</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Delta</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 260 }}>Evidence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {r.name}
                      {r.powerState && !/^powered ?on$/i.test(r.powerState) && (
                        <Chip size="small" label={r.powerState}
                          sx={{ ml: 0.5, height: 17, fontSize: 10, bgcolor: '#FFF4E5', color: AMBER, fontWeight: 700 }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: GREY, maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {[r.cluster, r.os].filter(Boolean).join(' · ') || DASH}
                    </TableCell>
                    <TableCell align="right">{fmt(r.vcpu)} / {fmt(r.ramGb)}</TableCell>
                    <TableCell align="right">
                      {fmt1(r.cpuAvg)} /{' '}
                      <b style={{ color: r.cpuMax !== null && r.cpuMax >= t.undersizedPeakCpu ? RED : NAVY }}>
                        {fmt1(r.cpuMax)}
                      </b>
                    </TableCell>
                    <TableCell align="right">
                      {fmt1(r.memAvg)} /{' '}
                      <b style={{ color: r.memMax !== null && r.memMax >= t.undersizedPeakMem ? RED : NAVY }}>
                        {fmt1(r.memMax)}
                      </b>
                    </TableCell>
                    <TableCell align="right">{fmt(r.totalIops)}</TableCell>
                    <TableCell><ClassChip cls={r.cls} /></TableCell>
                    <TableCell align="right">
                      {r.canSize ? (
                        <>
                          {fmt(r.targetVcpu)} / {fmt(r.targetRam)}
                          {(r.flooredVcpu || r.flooredRam) && (
                            <Tooltip title={`Computed demand fell below the ${MIN_VCPU} vCPU / ${MIN_RAM_GB} GB floor and was raised to it. Confirm the workload actually fits before committing.`}>
                              <Chip size="small" label="floor"
                                sx={{ ml: 0.5, height: 17, fontSize: 10, bgcolor: '#FFF4E5', color: AMBER, fontWeight: 700 }} />
                            </Tooltip>
                          )}
                        </>
                      ) : <span style={{ color: GREY }}>{DASH}</span>}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: r.canSize && r.deltaVcpu < 0 ? GREEN : r.canSize && r.deltaVcpu > 0 ? RED : GREY }}>
                      {r.canSize ? `${r.deltaVcpu > 0 ? '+' : ''}${r.deltaVcpu} vCPU / ${r.deltaRam > 0 ? '+' : ''}${fmt(r.deltaRam)} GB` : DASH}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: GREY }}>{r.why}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={visibleRows.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={PAGE_SIZES}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
          </TableContainer>

          <Typography sx={{ fontSize: 11, color: GREY, mt: 1, maxWidth: 1100 }}>
            Rendered rows are capped and paged; every count, total and export above covers the full set of{' '}
            {fmt(visibleRows.length)} matching servers. Candidate sizes are rounded UP to a purchasable cloud
            shape ({VCPU_LADDER.join(' / ')} vCPU, RAM to the next 4 GB), which keeps the reduction figure on the
            conservative side. Nothing on this screen is a p95, and nothing on it is a committed size.
          </Typography>
        </>
      )}
    </Box>
  );
}
